import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { transcriptService } from '../../services/transcript.service';
import { scheduleService } from '../../services/schedule.service';
import { planService } from '../../services/plan.service';
import { Transcript } from '../../types';
import { Modal } from '../UI/Modal';
import { Toast } from '../UI/Toast';
import {
  UploadIcon,
  FileIcon,
  TrashIcon,
  SparkleIcon,
  ArrowLeftIcon,
  CheckIcon,
  TargetIcon,
  LayersIcon,
} from '../UI/Icons';

const LEVEL_SEQUENCE = ['1A', '1B', '2A', '2B', '3A', '3B', '4A', '4B'];
const SEASONS = ['Spring', 'Fall', 'Winter'];

const TranscriptUpload: React.FC = () => {
  const navigate = useNavigate();
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [currentTranscript, setCurrentTranscript] = useState<Transcript | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [newPlanName, setNewPlanName] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  useEffect(() => {
    loadTranscripts();
  }, []);

  const loadTranscripts = async () => {
    try {
      const data = await transcriptService.getTranscripts();
      setTranscripts(data);
    } catch (error) {
      console.error('Failed to load transcripts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      await handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!file.name.endsWith('.pdf')) {
      setToast({ message: 'Please upload a PDF file', type: 'error' });
      return;
    }

    setUploading(true);
    try {
      const result = await transcriptService.uploadTranscript(file);
      setCurrentTranscript(result);
      await loadTranscripts();
      setToast({ message: 'Transcript processed successfully', type: 'success' });
    } catch (error) {
      console.error('Failed to upload transcript:', error);
      setToast({ message: 'Failed to upload transcript. Please try again.', type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleGeneratePlan = () => {
    if (!currentTranscript || !currentTranscript.terms || currentTranscript.terms.length === 0) {
      setToast({ message: 'No transcript data available to generate a plan.', type: 'error' });
      return;
    }

    const lastTerm = currentTranscript.terms[currentTranscript.terms.length - 1];
    const currentLevel = lastTerm.level || '1A';
    const levelIndex = LEVEL_SEQUENCE.indexOf(currentLevel);

    if (levelIndex === LEVEL_SEQUENCE.length - 1) {
      setToast({ message: 'Transcript already reached 4B. No future plan generated.', type: 'info' });
      return;
    }

    setNewPlanName('My Plan');
    setShowPlanModal(true);
  };

  const handleConfirmGeneratePlan = async () => {
    if (!newPlanName) {
      setToast({ message: 'Please enter a plan name', type: 'error' });
      return;
    }
    setShowPlanModal(false);
    await executePlanGeneration(newPlanName);
  };

  const executePlanGeneration = async (planName: string) => {
    if (!currentTranscript) return;

    const getTermYear = (name: string) => {
      const parts = name.split(' ');
      const y = parseInt(parts[parts.length - 1]);
      return isNaN(y) ? 0 : y;
    };

    const sortedTerms = [...(currentTranscript.terms || [])].sort((a, b) => {
      const yearA = getTermYear(a.name);
      const yearB = getTermYear(b.name);
      if (yearA !== yearB) return yearA - yearB;

      const seasons: { [key: string]: number } = { Winter: 1, Spring: 2, Fall: 3 };
      const seasonA = a.name.split(' ')[0];
      const seasonB = b.name.split(' ')[0];
      return (seasons[seasonA] || 0) - (seasons[seasonB] || 0);
    });

    const lastTerm = sortedTerms[sortedTerms.length - 1];
    if (!lastTerm) return;

    let currentYear = getTermYear(lastTerm.name);
    const parts = lastTerm.name.split(' ');
    let currentSeason = 'Fall';

    if (parts.length >= 2 && ['Fall', 'Winter', 'Spring'].includes(parts[0])) {
      currentSeason = parts[0];
    }

    if (currentYear === 0) {
      currentYear = new Date().getFullYear();
    }

    const currentLevel = lastTerm.level || '1A';
    let levelIndex = LEVEL_SEQUENCE.indexOf(currentLevel);

    if (levelIndex === -1) {
      levelIndex = 0;
    } else if (levelIndex === LEVEL_SEQUENCE.length - 1) {
      setToast({ message: 'Transcript already reached 4B. No future plan generated.', type: 'info' });
      return;
    }

    let seasonIndex = SEASONS.indexOf(currentSeason);
    if (seasonIndex === -1) seasonIndex = 0;

    setUploading(true);
    try {
      const newPlan = await planService.createPlan(planName);

      while (levelIndex < LEVEL_SEQUENCE.length - 1) {
        seasonIndex = (seasonIndex + 1) % 3;
        const nextSeason = SEASONS[seasonIndex];
        if (nextSeason === 'Winter') currentYear++;
        levelIndex++;

        await scheduleService.createSchedule({
          term: nextSeason,
          year: currentYear,
          term_number: LEVEL_SEQUENCE[levelIndex],
          is_planned: true,
          course_ids: [],
          plan_id: newPlan.id,
        });
      }

      setToast({ message: 'Academic plan generated successfully!', type: 'success' });
      setTimeout(() => navigate('/schedule'), 1500);
    } catch (error) {
      console.error('Failed to generate plan:', error);
      setToast({ message: 'Failed to generate academic plan.', type: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this transcript?')) {
      try {
        await transcriptService.deleteTranscript(id);
        await loadTranscripts();
        setToast({ message: 'Transcript deleted.', type: 'success' });
      } catch (error) {
        console.error('Failed to delete transcript:', error);
        setToast({ message: 'Failed to delete transcript.', type: 'error' });
      }
    }
  };

  const handleViewTranscript = async (id: number) => {
    try {
      const detail = await transcriptService.getTranscript(id);
      setCurrentTranscript(detail);
    } catch (error) {
      console.error('Failed to load transcript:', error);
      setToast({ message: 'Failed to load transcript.', type: 'error' });
    }
  };

  const planModal = (
    <Modal
      isOpen={showPlanModal}
      onClose={() => setShowPlanModal(false)}
      title="Generate academic plan"
      footer={
        <>
          <button className="btn-primary" onClick={handleConfirmGeneratePlan} disabled={uploading}>
            {uploading ? 'Generating…' : 'Generate plan'}
          </button>
          <button className="btn-secondary" onClick={() => setShowPlanModal(false)}>
            Cancel
          </button>
        </>
      }
    >
      <p className="text-sm text-canvas-600">
        We'll map out every term between where you are now and 4B. Give the plan a name so you can
        find it later.
      </p>
      <input
        type="text"
        className="input-field mt-4"
        placeholder="e.g. Co-op Focus, Fast Track…"
        value={newPlanName}
        onChange={(e) => setNewPlanName(e.target.value)}
        autoFocus
      />
    </Modal>
  );

  // ---------- Transcript review view ----------
  if (currentTranscript) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        {planModal}

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <button
              onClick={() => setCurrentTranscript(null)}
              className="mb-3 inline-flex items-center gap-1.5 text-sm text-canvas-600 transition-colors hover:text-canvas-900"
            >
              <ArrowLeftIcon width={16} height={16} />
              All transcripts
            </button>
            <h1 className="font-display text-2xl font-bold tracking-tight text-canvas-900 sm:text-3xl">
              Transcript Review
            </h1>
            <p className="mt-1.5 text-sm text-canvas-600">{currentTranscript.filename}</p>
          </div>
          <button onClick={handleGeneratePlan} disabled={uploading} className="btn-primary">
            <SparkleIcon width={16} height={16} />
            {uploading ? 'Generating…' : 'Generate plan'}
          </button>
        </div>

        {/* Summary stats */}
        <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { label: 'Student ID', value: currentTranscript.student_id || '—', icon: FileIcon },
            { label: 'Degree', value: currentTranscript.degree_type || '—', icon: LayersIcon },
            {
              label: 'Cumulative Average',
              value: currentTranscript.cgpa != null ? `${currentTranscript.cgpa}%` : '—',
              icon: TargetIcon,
            },
            {
              label: 'Major Average',
              value:
                currentTranscript.major_average != null
                  ? `${currentTranscript.major_average}%`
                  : '—',
              icon: TargetIcon,
            },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="card p-5">
              <div className="flex items-start justify-between">
                <span className="text-sm text-canvas-600">{label}</span>
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                  <Icon width={16} height={16} />
                </span>
              </div>
              <p className="mt-3 font-display text-xl font-bold tracking-tight text-canvas-900">
                {value}
              </p>
            </div>
          ))}
        </div>

        {/* Terms */}
        <div className="mt-6 space-y-4">
          {currentTranscript.terms?.map((term, termIndex) => (
            <div key={termIndex} className="card overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-canvas-200 bg-canvas-50 px-5 py-4">
                <div className="flex items-center gap-2.5">
                  {term.level && <span className="chip bg-ink text-white">{term.level}</span>}
                  <h3 className="font-display text-base font-semibold text-canvas-900">
                    {term.name}
                  </h3>
                </div>
                <div className="flex flex-wrap gap-4 text-xs text-canvas-600">
                  <span>
                    Term avg:{' '}
                    <span className="font-semibold text-canvas-900">
                      {term.stats?.term_gpa ?? '—'}
                    </span>
                  </span>
                  <span>
                    Cumulative:{' '}
                    <span className="font-semibold text-canvas-900">
                      {term.stats?.cumulative_gpa ?? '—'}
                    </span>
                  </span>
                  <span>
                    Major avg:{' '}
                    <span className="font-semibold text-canvas-900">
                      {term.stats?.major_average ?? '—'}
                    </span>
                  </span>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-canvas-200">
                      {['Course', 'Description', 'Credits', 'Grade'].map((h) => (
                        <th
                          key={h}
                          className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-canvas-500"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-canvas-200">
                    {term.courses.map((course, courseIndex) => (
                      <tr key={courseIndex} className="transition-colors hover:bg-canvas-50">
                        <td className="whitespace-nowrap px-5 py-3 text-sm font-medium text-canvas-900">
                          {course.course_code}
                        </td>
                        <td className="px-5 py-3 text-sm text-canvas-600">{course.course_name}</td>
                        <td className="whitespace-nowrap px-5 py-3 text-sm text-canvas-600">
                          {course.credits}
                        </td>
                        <td className="whitespace-nowrap px-5 py-3">
                          {course.grade ? (
                            <span
                              className={`chip ${
                                Number(course.grade) >= 80
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : Number(course.grade) >= 60
                                  ? 'bg-primary-50 text-primary-700'
                                  : 'bg-canvas-100 text-canvas-700'
                              }`}
                            >
                              {course.grade}
                            </span>
                          ) : (
                            <span className="text-sm text-canvas-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ---------- Upload / list view ----------
  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {planModal}

      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-canvas-900 sm:text-3xl">
          Transcript
        </h1>
        <p className="mt-1.5 text-sm text-canvas-600">
          Upload your transcript PDF to unlock grades, averages, and plan generation.
        </p>
      </div>

      {/* Upload area */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`mt-6 rounded-3xl border-2 border-dashed p-10 text-center transition-all ${
          dragActive
            ? 'border-primary-400 bg-primary-50'
            : 'border-canvas-300 bg-white hover:border-canvas-400'
        }`}
      >
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
          <UploadIcon width={24} height={24} />
        </span>
        <p className="mt-4 font-display text-lg font-semibold text-canvas-900">
          {uploading ? 'Processing your transcript…' : 'Drop your transcript here'}
        </p>
        <p className="mt-1.5 text-sm text-canvas-600">
          PDF only — the unofficial transcript from Quest works best
        </p>
        <label className="mt-5 inline-block cursor-pointer">
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            disabled={uploading}
            className="hidden"
          />
          <span className="btn-primary">
            {uploading ? 'Uploading…' : 'Select file'}
          </span>
        </label>
      </div>

      {/* Transcript list */}
      <div className="mt-8">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="h-20 animate-pulse rounded-2xl bg-canvas-200/70" />
            ))}
          </div>
        ) : transcripts.length === 0 ? (
          <div className="card flex flex-col items-center px-6 py-14 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-canvas-100 text-canvas-500">
              <FileIcon width={26} height={26} />
            </span>
            <p className="mt-4 font-medium text-canvas-900">No transcripts uploaded</p>
            <p className="mt-1 text-sm text-canvas-600">
              Upload your first transcript to get started
            </p>
          </div>
        ) : (
          <>
            <h2 className="font-display text-base font-semibold text-canvas-900">
              Uploaded transcripts
            </h2>
            <div className="mt-3 space-y-3">
              {transcripts.map((transcript) => (
                <div
                  key={transcript.id}
                  onClick={() => handleViewTranscript(transcript.id)}
                  className="card group flex cursor-pointer items-center gap-4 p-4 transition-all hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-card-hover"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
                    <FileIcon width={20} height={20} />
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-canvas-900">
                      {transcript.filename}
                    </p>
                    <p className="text-xs text-canvas-500">
                      Uploaded {new Date(transcript.uploaded_at || '').toLocaleDateString()}
                      {transcript.courses_extracted?.length
                        ? ` · ${transcript.courses_extracted.length} courses`
                        : ''}
                    </p>
                  </div>

                  <span
                    className={`chip shrink-0 ${
                      transcript.processed
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-amber-50 text-amber-700'
                    }`}
                  >
                    {transcript.processed && <CheckIcon width={12} height={12} />}
                    {transcript.processed ? 'Processed' : 'Processing'}
                  </span>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(transcript.id);
                    }}
                    className="shrink-0 rounded-lg p-2 text-canvas-400 transition-colors hover:bg-rose-50 hover:text-rose-500"
                    aria-label="Delete transcript"
                  >
                    <TrashIcon width={17} height={17} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TranscriptUpload;
